import { ORPCError } from '@orpc/client'
import type {
  PlatformRole,
  PlatformUser,
  TenantRole,
  UserCreateInput,
} from '@sirius/contracts/admin'
import {
  platformRoleSchema,
  tenantRoleSchema,
  userCreateInputSchema,
} from '@sirius/contracts/admin'
import type { TFunction } from 'i18next'
import { z } from 'zod'
import { roleLabel } from './userFormat'

/** The tenant select's value for "no membership". */
export const NO_TENANT = 'none'

/**
 * The Create user dialog's fields: the API's input, with the first
 * membership as a tenant (`NO_TENANT` for none) and a role.
 */
export const createUserFormSchema = userCreateInputSchema
  .omit({ membership: true })
  .extend({
    platformRole: platformRoleSchema.nullable(),
    tenantId: z.string(),
    role: tenantRoleSchema,
  })
export type CreateUserFormValues = z.input<typeof createUserFormSchema>
export type CreateUserFormOutput = z.output<typeof createUserFormSchema>

/** What `users.create` gets from the dialog. */
export function toCreateUserInput({
  tenantId,
  role,
  ...values
}: CreateUserFormOutput): UserCreateInput {
  return tenantId === NO_TENANT
    ? values
    : { ...values, membership: { tenantId, role } }
}

/**
 * A refused create that one field of the dialog explains (a taken email:
 * CONFLICT); null for any other error, which the dialog shows as a whole.
 */
export function createUserFieldError(
  error: unknown,
  t: TFunction,
): { field: 'email'; message: string } | null {
  return error instanceof ORPCError && error.code === 'CONFLICT'
    ? { field: 'email', message: t('usersPage.create.emailTaken') }
    : null
}

/**
 * Set, change or remove a platform role on the user page. A platform admin
 * never changes their own (the API refuses it too), so the picker shows
 * disabled with why.
 */
export function platformRoleActionFor(
  user: Pick<PlatformUser, 'id'>,
  viewerId: string | undefined,
  t: TFunction,
): { disabledReason: string | null } {
  return {
    disabledReason:
      user.id === viewerId
        ? t('usersPage.platformRoleChange.selfBlocked')
        : null,
  }
}

const NO_ROLE = 'none'

/** How high a platform role ranks: lowering one is destructive. */
function rank(role: PlatformRole | null) {
  return role === null ? 0 : ROLE_RANKS[role]
}

const ROLE_RANKS: Record<PlatformRole, number> = { technician: 1, admin: 2 }

/** What the confirmation of a platform role change says. */
export function platformRoleChange(
  from: PlatformRole | null,
  to: PlatformRole | null,
  t: TFunction,
  userName: string,
): {
  title: string
  /** the title with the user's name, for the confirmation */
  confirmTitle: string
  description: string
  confirmLabel: string
  destructive: boolean
} {
  const description = t(
    `usersPage.platformRoleChange.descriptions.${to ?? NO_ROLE}`,
  )
  const role = to === null ? null : roleLabel(to, t)
  return {
    title:
      role === null
        ? t('usersPage.platformRoleChange.removeTitle')
        : t('usersPage.platformRoleChange.makeTitle', { role }),
    confirmTitle:
      role === null
        ? t('usersPage.platformRoleChange.removeConfirmTitle', {
            name: userName,
          })
        : t('usersPage.platformRoleChange.makeConfirmTitle', {
            role,
            name: userName,
          }),
    description,
    confirmLabel: t('usersPage.platformRoleChange.confirmLabel'),
    destructive: rank(to) < rank(from),
  }
}

/** The tenants the user may still join: those without a membership. */
export function tenantsToJoin<Tenant extends { id: string }>(
  tenants: readonly Tenant[],
  memberships: PlatformUser['memberships'],
): Tenant[] {
  const joined = new Set(memberships.map(({ tenantId }) => tenantId))
  return tenants.filter(({ id }) => !joined.has(id))
}

/** The tenant roles, as the selects offer them (owner: staff only). */
export const TENANT_ROLE_OPTIONS: readonly TenantRole[] =
  tenantRoleSchema.options
